import {
  Box,
  Card,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  Typography,
  Zoom,
} from '@mui/material';
import {useFormik} from 'formik';
import {validationSchemaSetPassword} from '../../helpers/formValidator';
import InputElement from '@jobseeker/lotus/elements/Input';
import {VersionVariants} from '@jobseeker/lotus/enums/variants';
import {useEffect, useState} from 'react';
import {Image} from '@jobseeker/lotus/components/Image';
import {
  Eye,
  HiddenEye,
  OrangeCheckCircle,
  SurveyCheckGreen,
} from '@jobseeker/lotus/assets/icons';
import Button from '@jobseeker/lotus/elements/ButtonV2';
import {useWrappedTheme} from '@jobseeker/lotus/hooks';
import {resetPassword} from '../../helpers/auth';
import Loader from '@jobseeker/lotus/components/Loader';
import {SetPasswordProps} from '@jobseeker/feature-authentication/components/types';
import {useValidateComponent} from '@jobseeker/lotus/hooks';
import {TrackEventUBA, getUbaPageName} from '@jobseeker/common-core/utils';

const Input = ({InputProps, ...props}: any) => (
  <InputElement
    InputProps={{style: {fontSize: 10}, ...InputProps}}
    variant={VersionVariants.VERSION_TWO}
    sx={{
      mt: 1,
      input: {padding: '14px 16px'},
      '.MuiInputBase-root': {background: '#F8F8F8'},
    }}
    {...props}
  />
);

const PLATFORM = process.env.NEXT_PUBLIC_APP_NAME;
const isAppHirist = PLATFORM === 'hirist';

const SetPassword = ({
  handleLoginCTA,
  forConsent,
  onPasswordSetSuccess,
}: SetPasswordProps & {onPasswordSetSuccess?: () => void}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [apiError, setApiError] = useState<{message?: string; code?: number}>(
    {}
  );
  const [loading, setLoading] = useState(false);
  const [passwordSetSuccess, setPasswordSetSuccess] = useState('');
  const [handlePasswordRegexValidation, PasswordValidationComponent] =
    useValidateComponent();

  const theme = useWrappedTheme();
  const pageName = getUbaPageName();

  useEffect(() => {
    TrackEventUBA('resetPassView', 'view', {
      pageName,
      sessionUsername: sessionStorage.getItem('userId') || '',
    });
  }, []);

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema: validationSchemaSetPassword,
    onSubmit: async values => {
      handleResetPassword(values.password, values.confirmPassword);
    },
    validate: async values => {
      setApiError({});
      handlePasswordRegexValidation(values.password);
    },
  });

  const handleResetPassword = async (
    password: string,
    confirmPassword: string
  ) => {
    try {
      setLoading(true);
      const message = await resetPassword(
        password,
        confirmPassword,
        forConsent ? true : false
      );
      setPasswordSetSuccess(message);
      TrackEventUBA('resetPassClick', 'click', {
        pageName,
        sessionUsername: sessionStorage.getItem('userId') || '',
        status: 'success',
      });
      setLoading(false);

      if (onPasswordSetSuccess) {
        onPasswordSetSuccess();
      }
    } catch (error) {
      if (typeof error === 'object' && error) {
        setApiError(error);
      }
      TrackEventUBA('resetPassClick', 'click', {
        pageName,
        sessionUsername: sessionStorage.getItem('userId') || '',
        status: 'failure',
      });
      setLoading(false);
    }
  };

  const Wrapper = (forConsent ? Box : Card) as React.ElementType;

  return (
    <Wrapper
      sx={{
        position: 'relative',
        transition: 'all ease 0.2s',
        boxShadow: forConsent ? 'none' : 2,
        borderRadius: 4,
        mx: {
          md: forConsent ? 0 : passwordSetSuccess ? 24 : 14,
          lg: forConsent ? 0 : passwordSetSuccess ? 44 : 24,
          xl: forConsent ? 0 : passwordSetSuccess ? 52 : 32,
        },
        display: {xxs: 'block', md: 'flex'},
        justifyContent: forConsent
          ? 'center'
          : {xxs: 'center', md: 'space-between'},
      }}
    >
      {!passwordSetSuccess && (
        <>
          <Box
            component={'form'}
            noValidate
            onSubmit={event => {
              event.preventDefault();
              formik.handleSubmit();
            }}
            sx={{
              px: {xxs: 2, md: 4, lg: 8},
              py: {xxs: 2, md: 4, lg: 5},
              width: {xxs: '100%', sm: 440, md: 472, lg: 536},
            }}
          >
            <Typography variant="subtitle1">
              {forConsent ? 'Set New Password' : 'Reset Password'}
            </Typography>
            <FormControl
              sx={{width: '100%', mt: 3}}
              error={formik.touched.password && Boolean(formik.errors.password)}
              key={'password'}
            >
              <Typography variant="subtitle3">New Password</Typography>
              <Input
                type={showPassword ? 'text' : 'Password'}
                name="password"
                key="password_set"
                value={formik.values.password}
                onChange={formik.handleChange}
                error={
                  formik.touched.password && Boolean(formik.errors.password)
                }
                placeholder="Enter Password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        // onMouseDown={handleMouseDown}
                        // edge="end"
                      >
                        {showPassword ? (
                          <Image
                            src={Eye}
                            width={15}
                            height={15}
                            alt="show password"
                          />
                        ) : (
                          <Image
                            src={HiddenEye}
                            width={15}
                            height={15}
                            alt="hide password"
                          />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {formik.touched.password && Boolean(formik.errors.password) && (
                <FormHelperText>{formik.errors.password}</FormHelperText>
              )}
            </FormControl>

            <FormControl
              sx={{width: '100%', mt: 3}}
              error={
                formik.touched.confirmPassword &&
                Boolean(formik.errors.confirmPassword)
              }
              key={'confirm-password'}
            >
              <Typography variant="subtitle3">Confirm New Password</Typography>
              <Input
                type={showConfirmPassword ? 'text' : 'Password'}
                name="confirmPassword"
                key="confirm_password_set"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                error={
                  formik.touched.confirmPassword &&
                  Boolean(formik.errors.confirmPassword)
                }
                placeholder="Enter Password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        // onMouseDown={handleMouseDown}
                        // edge="end"
                      >
                        {showPassword ? (
                          <Image
                            src={Eye}
                            width={15}
                            height={15}
                            alt="show password"
                          />
                        ) : (
                          <Image
                            src={HiddenEye}
                            width={15}
                            height={15}
                            alt="hide password"
                          />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {formik.touched.confirmPassword &&
                Boolean(formik.errors.confirmPassword) && (
                  <FormHelperText>
                    {formik.errors.confirmPassword}
                  </FormHelperText>
                )}
            </FormControl>
            {apiError &&
              apiError.message !== '' &&
              apiError.message !== undefined && (
                <Typography
                  component="p"
                  fontSize="13px"
                  color={'error.main'}
                  display="inline-flex"
                  marginTop="10px"
                >
                  {apiError.message}
                </Typography>
              )}
            <Button
              type="submit"
              sx={{
                width: {xxs: '100%', md: '200px'},
                background: '#333333',
                mt: {xxs: 3, md: 4},
              }}
              variant="contained"
              size="large"
              disabled={loading}
            >
              Confirm
            </Button>
          </Box>
          <Box
            border={`1px solid #7E7E7E33`}
            my={{xxs: 2, md: 6}}
            marginLeft={{xxs: 2, md: 0}}
            marginRight={{xxs: 2, md: 5, lg: 11.25}}
            borderRadius={4}
            py={{xxs: 1, md: 3.25}}
            px={2}
            sx={{
              background: '#F8F8F8',
              maxWidth: 185,
              maxHeight: 240,
            }}
          >
            <PasswordValidationComponent />
          </Box>
        </>
      )}
      {passwordSetSuccess && (
        <Box
          sx={{
            px: {xxs: 2, md: 4, lg: 8},
            py: {xxs: 2, md: 4, lg: 5},
          }}
        >
          <Box display={'flex'}>
            <Image
              src={isAppHirist ? OrangeCheckCircle : SurveyCheckGreen}
              width={48}
              height={48}
              alt="successIcon"
            />

            <Box ml={1}>
              <Typography
                variant="subtitle1"
                fontWeight={theme.typography.fontWeightBold}
              >
                Your password has been successfully changed!
              </Typography>
              <Typography variant="subtitle2">
                For your security, you have been logged out from all devices
              </Typography>
              <Typography variant="subtitle2">
                Use your new password to login to your account
              </Typography>

              <Box display="flex" justifyContent={'flex-start'}>
                <Button
                  sx={{
                    width: {xxs: '100%', md: '200px'},
                    background: '#333333',
                    mt: 2,
                  }}
                  variant="contained"
                  size="large"
                  disabled={loading}
                  onClick={() => {
                    handleLoginCTA();
                  }}
                >
                  {loading ? (
                    <Loader size={35} sx={{color: '#ffffff !important'}} />
                  ) : (
                    'Login'
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Wrapper>
  );
};

export default SetPassword;
